import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "../../../contexts/CartContext";

const PRICE_TYPE_KEYS = {
  1: "hour",
  2: "person",
  3: "day",
  4: "unit",
  5: "fixed",
};

const AddToCartModal = ({ product, onClose, eventDates }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const { handleAddToCart } = useCart();
  const [selectedOptions, setSelectedOptions] = useState({});

  const priceTypeKey = PRICE_TYPE_KEYS[product.priceTypeId] || "unit";
  const inventoryMode =
    product.productInventoryLimits?.inventoryMode || "unlimited";
  const totalAvailable =
    product.productInventoryLimits?.totalQuantity || Infinity;

  const isFixedPrice = priceTypeKey === "fixed";
  const isPerHour = priceTypeKey === "hour";
  const isPerPerson = priceTypeKey === "person";
  const isPerDay = priceTypeKey === "day";
  const isPerUnit = priceTypeKey === "unit";

  const isUnlimited = inventoryMode === "unlimited";
  const isFixedInventory = inventoryMode === "fixed";
  const isDailyInventory = inventoryMode === "daily";

  const requiresDates = isFixedPrice || isPerHour || isPerPerson || isPerDay;

  const showOptions =
    priceTypeKey === "unit" && (product.productConfigurations?.length || 0) > 0;

  const getSelectedConfiguration = () => {
    const config = product.productConfigurations?.[0]; // une seule option à la fois
    const selectedIndex = selectedOptions[config?.id];
    return config?.values?.[selectedIndex];
  };

  const getAvailableQuantity = (date = null) => {
    const selectedVal = getSelectedConfiguration();
    const isUnit = priceTypeKey === "unit";

    const optionsHaveQuantities =
      product.productConfigurations?.[0]?.values?.some(
        (val) => val.quantity != null && val.quantity !== ""
      );

    const quantityUsedElsewhere = Object.entries(quantitiesByDay)
      .filter(([d]) => d !== date)
      .reduce((sum, [, val]) => sum + parseInt(val || 0), 0);

    // ✅ Si options avec quantité → on ne regarde pas le stock global
    if (optionsHaveQuantities && selectedVal?.quantity != null) {
      return isUnit
        ? selectedVal.quantity
        : Math.max(0, selectedVal.quantity - quantityUsedElsewhere);
    }

    // ✅ Sinon, on regarde le stock global
    if ((isFixedInventory || isDailyInventory) && !optionsHaveQuantities) {
      return isUnit
        ? totalAvailable
        : Math.max(0, totalAvailable - quantityUsedElsewhere);
    }

    if (isPerHour) return 24;

    return Infinity;
  };

  useEffect(() => {
    const input = document.querySelector("input[type='number']");
    if (input) input.focus();
  }, []);

  const handleOptionSelect = (optionId, index) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionId]: index,
    }));
  };

  const availableDays =
    product.allAvailableDates?.map((d) => {
      const date = new Date(d);
      date.setHours(0, 0, 0, 0);
      return date.toLocaleDateString("fr-CA");
    }) || [];
  const [quantitiesByDay, setQuantitiesByDay] = useState({});
  const [globalQuantity, setGlobalQuantity] = useState(1); // for unit-based pricing

  const filteredDates = useMemo(() => {
    if (!eventDates?.eventDate || !eventDates?.eventEndDate) return [];

    const start = new Date(eventDates.eventDate);
    const end = new Date(eventDates.eventEndDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const result = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toLocaleDateString("fr-CA");
      if (availableDays.includes(dateStr)) result.push(dateStr);
    }

    return result;
  }, [eventDates, availableDays]);

  const allOptionsOutOfStock =
    product.productConfigurations?.[0]?.values?.every(
      (val) => val.quantity === 0
    ) ?? false;

  useEffect(() => {
    if (
      (isPerHour || isPerPerson) &&
      availableDays.length > 0 &&
      !Object.keys(quantitiesByDay).length
    ) {
      const initial = Object.fromEntries(availableDays.map((d) => [d, 0]));
      setQuantitiesByDay(initial);
    }
  }, [isPerHour, isPerPerson, availableDays]);

  const calculateTotalQuantity = () => {
    if (isPerUnit) return globalQuantity;
    return Object.values(quantitiesByDay).reduce(
      (sum, val) => sum + parseInt(val || 0),
      0
    );
  };

  const calculateTotalPrice = () => {
    const base = product.price || 0;
    const qty = calculateTotalQuantity();

    const surplus =
      product.productConfigurations?.reduce((sum, config) => {
        const selectedIndex = selectedOptions?.[config.id];
        const selectedVal = config?.values?.[selectedIndex];
        return sum + (selectedVal?.price || 0);
      }, 0) || 0;

    return ((base + surplus) * qty).toFixed(2);
  };

  const handleQuantityChange = (date, value) => {
    let val = parseInt(value) || 0;
    if (isPerHour) {
      val = Math.min(val, 24); // ✅ on limite à 24h max
    }
    setQuantitiesByDay((prev) => ({
      ...prev,
      [date]: Math.max(0, val),
    }));
  };

  const handleAdd = () => {
    const quantity = calculateTotalQuantity();
    const selectedVal = getSelectedConfiguration();
    const payload = {
      ...product,
      quantity,
      perDay: requiresDates ? quantitiesByDay : null,
      totalPrice: parseFloat(calculateTotalPrice()),
      OptionSurplus: selectedVal?.price || 0,
      OptionName: selectedVal
        ? lang === "fr"
          ? selectedVal.nameFr
          : selectedVal.nameEn
        : null,
      selectedOptionId: selectedVal?.id || null,
    };

    onClose(payload);
  };

  const isCompletelyOutOfStock =
    (isPerUnit && allOptionsOutOfStock) ||
    (!isPerUnit && calculateTotalQuantity() === 0);

  useEffect(() => {
    if (
      showOptions &&
      product.productConfigurations?.length > 0 &&
      Object.keys(selectedOptions).length === 0
    ) {
      const config = product.productConfigurations[0];
      const firstAvailableIndex = config.values.findIndex(
        (val) => val.quantity > 0
      );
      if (firstAvailableIndex !== -1) {
        setSelectedOptions({
          [config.id]: firstAvailableIndex,
        });
      }
    }
  }, [product, showOptions, selectedOptions]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 text-primary">
      <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 relative">
        <button
          onClick={() => onClose(null)}
          className="absolute top-4 right-4 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-2">
          {lang === "fr" ? product.nameFr : product.nameEn}
        </h2>
        <p className="text-gray-600 text-sm mb-4">
          {lang === "fr" ? product.descriptionFr : product.descriptionEn}
        </p>

        <div className="mb-4">
          <p className="font-semibold">{t("cart.basePrice")}</p>
          <p>${product.price.toFixed(2)}</p>
        </div>

        {showOptions &&
          product.productConfigurations.map((option) => {
            const selectedIndex = selectedOptions?.[option.id];
            const selectedVal = option.values?.[selectedIndex];

            return (
              <div key={option.id} className="">
                <p className="font-semibold capitalize mb-2">
                  {t("modal." + option.type)}
                </p>

                {/* Pastilles */}
                <div className="flex flex-wrap gap-2">
                  {option.values.map((val, i) => {
                    const isSelected = selectedIndex === i;
                    const isDisabled = val.quantity === 0;
                    const isColorType = option.type === "color";
                    const colorCode = val.color || "#ccc";

                    return (
                      <div
                        key={val.id}
                        title={isDisabled ? t("cart.outOfStock") : ""}
                        onClick={() =>
                          !isDisabled && handleOptionSelect(option.id, i)
                        }
                        className={`relative flex flex-col items-center ${
                          isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                        style={isColorType ? { width: "36px" } : {}}
                      >
                        {/* Badge prix */}
                        {val.price > 0 && (
                          <div className="absolute -top-3 text-xs bg-black text-white px-2 py-0.5 rounded-full shadow">
                            +{val.price.toFixed(2)}$
                          </div>
                        )}

                        {/* Couleur ou texte */}
                        {isColorType ? (
                          <div
                            className={`w-8 h-8 rounded-full mb-1 opacity-100 relative ${
                              isSelected
                                ? "ring-2 ring-primary"
                                : "border-2 border-gray-500"
                            }`}
                            style={{
                              backgroundColor: colorCode,
                            }}
                          >
                            {/* Trait rouge si disabled */}
                            {isDisabled && (
                              <div className="absolute w-full h-0.5 bg-red-600 top-1/2 left-0 transform -translate-y-1/2 rotate-45"></div>
                            )}
                          </div>
                        ) : (
                          <div
                            className={`px-3 py-1 rounded-full border text-sm max-w-[200px] text-center whitespace-nowrap ${
                              isSelected
                                ? "border-muted bg-muted text-white"
                                : "border-gray-300 text-gray-800"
                            }`}
                          >
                            {lang === "fr" ? val.nameFr : val.nameEn}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Message global si aucune option en stock */}
                {option.values.every((val) => val.quantity === 0) && (
                  <p className="mt-2 text-sm font-medium text-red-700 bg-red-100 px-3 py-1 rounded shadow inline-block">
                    {t("cart.outOfStock")}
                  </p>
                )}
              </div>
            );
          })}

        {requiresDates && (eventDates || isPerPerson || isPerHour) && (
          <div className="mb-6">
            <label className="block font-semibold mb-2">
              {t("cart.quantityPerDay", {
                label: t(`priceTypes.${priceTypeKey}`),
              })}
            </label>
            <div className="space-y-2">
              {filteredDates.map((date) => {
                const disabled = !availableDays.includes(date);
                const selectedVal = getSelectedConfiguration();
                const remainingOptionQty =
                  selectedVal?.quantity != null
                    ? Math.max(
                        0,
                        selectedVal.quantity -
                          Object.entries(quantitiesByDay).reduce(
                            (sum, [d, val]) =>
                              d !== date ? sum + parseInt(val || 0) : sum,
                            0
                          )
                      )
                    : null;

                const remainingGlobalQty =
                  isFixedInventory || isDailyInventory
                    ? Math.max(
                        0,
                        totalAvailable -
                          Object.entries(quantitiesByDay).reduce(
                            (sum, [d, val]) =>
                              d !== date ? sum + parseInt(val || 0) : sum,
                            0
                          )
                      )
                    : null;

                const max = getAvailableQuantity(date);

                return (
                  <div key={date} className="flex items-center gap-3">
                    <span className="w-36 text-sm text-gray-700">
                      {new Date(date).toLocaleDateString(
                        lang === "fr" ? "fr-CA" : "en-CA",
                        {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        }
                      )}
                    </span>

                    {isFixedPrice || isPerDay ? (
                      <input
                        type="checkbox"
                        checked={quantitiesByDay[date] === 1}
                        onChange={(e) =>
                          handleQuantityChange(date, e.target.checked ? 1 : 0)
                        }
                        className="form-checkbox h-5 w-5 text-primary"
                      />
                    ) : isPerHour || isPerPerson ? (
                      isUnlimited ? (
                        <input
                          type="number"
                          value={quantitiesByDay[date]}
                          onChange={(e) =>
                            handleQuantityChange(date, e.target.value)
                          }
                          min={0}
                          max={isPerHour ? 24 : undefined}
                          className="border px-3 py-1 rounded w-20"
                          onFocus={(e) => e.target.select()}
                        />
                      ) : (
                        <input
                          type="number"
                          value={quantitiesByDay[date]}
                          onChange={(e) =>
                            handleQuantityChange(
                              date,
                              Math.min(max, parseInt(e.target.value) || 0)
                            )
                          }
                          min={0}
                          max={max}
                          className="border px-3 py-1 rounded w-20"
                          onFocus={(e) => e.target.select()}
                        />
                      )
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isPerUnit && !allOptionsOutOfStock && (
          <div className="mb-4">
            <label className="block font-semibold mb-1">
              {t("cart.quantity", { label: t(`priceTypes.unit`) })}
            </label>
            {isUnlimited ? (
              <input
                type="number"
                value={globalQuantity}
                onChange={(e) =>
                  setGlobalQuantity(Math.max(1, parseInt(e.target.value) || 1))
                }
                className="border px-3 py-1 rounded w-24"
                min={0}
              />
            ) : (
              (() => {
                const selectedVal =
                  product.productConfigurations?.[0]?.values?.[
                    selectedOptions?.[product.productConfigurations?.[0]?.id]
                  ];
                const maxQuantity = Number.isFinite(selectedVal?.quantity)
                  ? selectedVal.quantity
                  : Number.isFinite(totalAvailable)
                  ? totalAvailable
                  : 0;

                return (
                  <select
                    value={globalQuantity}
                    onChange={(e) =>
                      setGlobalQuantity(parseInt(e.target.value))
                    }
                    className="border px-3 py-1 rounded"
                  >
                    {Array.from({ length: maxQuantity + 1 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                );
              })()
            )}
          </div>
        )}

        <div className="mt-6 text-right flex justify-between items-center">
          <p className="text-lg font-semibold">
            Total:{" "}
            <span className="text-primary">${calculateTotalPrice()}</span>
          </p>
          <button
            onClick={handleAdd}
            disabled={allOptionsOutOfStock}
            className={`px-6 py-2 rounded-full transition ${
              isCompletelyOutOfStock
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-primary text-white hover:bg-primary/90"
            }`}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToCartModal;
